import { format } from 'date-fns'

import { parseDateOnly } from '@/lib/formatters'
import { type LossRunClaim, LossRunClaimCoverageType } from '@/models/lossRunRequest'

export const CLAIM_COVERAGE_LABELS: Record<LossRunClaimCoverageType, string> = {
  [LossRunClaimCoverageType.AUTO_LIABILITY]: 'Auto Liability',
  [LossRunClaimCoverageType.AUTO_PHYSICAL_DAMAGE]: 'Physical Damage',
  [LossRunClaimCoverageType.GENERAL_LIABILITY]: 'General Liability',
  [LossRunClaimCoverageType.MOTOR_TRUCK_CARGO]: 'Motor Truck Cargo',
  [LossRunClaimCoverageType.UNKNOWN]: 'Unknown',
}

export const CLAIM_COVERAGE_OPTIONS = Object.values(LossRunClaimCoverageType).map((value) => ({
  value,
  label: CLAIM_COVERAGE_LABELS[value],
}))

export function claimCoverageLabel(coverageType: string): string {
  return (CLAIM_COVERAGE_LABELS as Partial<Record<string, string>>)[coverageType] ?? coverageType
}

export interface ClaimFormState {
  coverageType: LossRunClaimCoverageType
  incidentDate: Date | undefined
  claimCount: string
  paidLoss: string
  reserve: string
}

export interface ClaimPayload {
  coverageType: string
  incidentDate: string | null
  claimCount: number | null
  paidLoss: string | null
  reserve: string | null
}

export const EMPTY_CLAIM_FORM: ClaimFormState = {
  coverageType: LossRunClaimCoverageType.AUTO_LIABILITY,
  incidentDate: undefined,
  claimCount: '',
  paidLoss: '',
  reserve: '',
}

export function claimToForm(claim: LossRunClaim): ClaimFormState {
  return {
    coverageType: claim.coverageType,
    incidentDate: parseDateOnly(claim.incidentDate),
    claimCount: claim.claimCount != null ? String(claim.claimCount) : '',
    paidLoss: claim.paidLoss ?? '',
    reserve: claim.reserve ?? '',
  }
}

/** Why the form can't be saved as it is, or null. */
export function claimFormError(form: ClaimFormState): string | null {
  for (const [field, label] of [
    ['paidLoss', 'Paid loss'],
    ['reserve', 'Reserve'],
  ] as const) {
    const raw = form[field]
    if (raw !== '' && (isNaN(parseFloat(raw)) || parseFloat(raw) < 0))
      return `${label} must be a number of 0 or more`
  }
  const count = parseInt(form.claimCount, 10)
  if (form.claimCount !== '' && (isNaN(count) || count < 0))
    return 'Claim count must be a whole number of 0 or more'
  return null
}

export function claimFormToPayload(form: ClaimFormState): ClaimPayload {
  return {
    coverageType: form.coverageType,
    incidentDate: form.incidentDate ? format(form.incidentDate, 'yyyy-MM-dd') : null,
    claimCount: form.claimCount === '' ? null : parseInt(form.claimCount, 10),
    paidLoss: form.paidLoss === '' ? null : form.paidLoss,
    reserve: form.reserve === '' ? null : form.reserve,
  }
}
